import { useCallback, useEffect, useState } from "react";
import { BookOpen, CalendarCheck, ChartBar, ChatsCircle, CurrencyInr, UploadSimple, UserCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import DashboardShell from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { API, formatErr, inr } from "@/lib/api";
import DirectChat from "@/components/messaging/DirectChat";
import Student360View from "@/components/Student360View";
import OrganizationInbox from "@/components/messaging/OrganizationInbox";
import UpcomingClassesCalendar from "@/components/UpcomingClassesCalendar";

const NAV = [{ id: "overview", label: "My learning", icon: BookOpen }, { id: "lessons", label: "Lessons & Work", icon: BookOpen }, { id: "chat", label: "Teacher Chat", icon: ChatsCircle }, { id: "help", label: "Help & Suggestions", icon: ChatsCircle }, { id: "fees", label: "My Fees", icon: CurrencyInr }, { id: "profile", label: "My Profile", icon: UserCircle }];

export default function Student() {
  const [data, setData] = useState(null);
  const [codes, setCodes] = useState({});
  const [busy, setBusy] = useState("");
  const [tab, setTab] = useState("overview");
  const [submissionNotes, setSubmissionNotes] = useState({});
  const [submissionFiles, setSubmissionFiles] = useState({});

  const load = useCallback(async () => {
    try {
      const response = await api.get("/student/dashboard");
      setData(response.data);
    } catch (error) {
      toast.error(formatErr(error, "Could not load student portal"));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markAttendance = async (sessionId) => {
    if (!codes[sessionId]?.trim()) return toast.error("Enter the attendance code shown by your faculty");
    setBusy(sessionId);
    try {
      await api.post(`/learning/sessions/${sessionId}/self-attendance`, { attendance_code: codes[sessionId].trim() });
      toast.success("Attendance marked successfully");
      await load();
    } catch (error) {
      toast.error(formatErr(error));
    } finally {
      setBusy("");
    }
  };

  const submitWork = async (lessonId) => {
    setBusy(`submit-${lessonId}`);
    try {
      let attachment_path = null;
      const file = submissionFiles[lessonId];
      if (file) {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/files/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        attachment_path = data.path;
      }
      await api.post("/learning/submissions", { curriculum_item_id: lessonId, notes: submissionNotes[lessonId] || "", attachment_path });
      toast.success("Work submitted to your teacher");
      setSubmissionNotes({ ...submissionNotes, [lessonId]: "" });
      setSubmissionFiles({ ...submissionFiles, [lessonId]: null });
      await load();
    } catch (error) { toast.error(formatErr(error)); }
    finally { setBusy(""); }
  };

  const openResource = async (path) => {
    try {
      const { data } = await api.get("/files/sign", { params: { path } });
      window.open(`${API}/files/download?path=${encodeURIComponent(path)}&dt=${encodeURIComponent(data.token)}`, "_blank", "noopener,noreferrer");
    } catch (error) { toast.error(formatErr(error, "Attachment unavailable")); }
  };

  const sessionAttendance = Object.fromEntries((data?.attendance || []).filter((item) => item.session_id).map((item) => [item.session_id, item]));

  return (
    <DashboardShell brand="Student Portal" nav={NAV} active={tab} onNavigate={setTab}>
      <div data-testid="student-dashboard">
        <div className="mb-6">
          <h1 className="font-heading text-2xl font-extrabold">Welcome, {data?.student?.name || "Student"}</h1>
          <p className="text-sm text-slate-500 mt-1">{data?.academic_year?.name || "No current academic year"}{data?.enrolment?.semester ? ` · ${data.enrolment.semester}` : ""}</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Stat icon={BookOpen} label="Sessions" value={data?.sessions?.length || 0} />
          <Stat icon={CalendarCheck} label="Attendance records" value={data?.attendance?.length || 0} />
          <Stat icon={ChartBar} label="Progress updates" value={data?.progress?.length || 0} />
          <Stat icon={CurrencyInr} label="Fees outstanding" value={inr(data?.fees_outstanding || 0)} />
        </div>

        {tab === "overview" && <div className="space-y-5"><UpcomingClassesCalendar sessions={data?.sessions || []} title="My upcoming classes"/><div className="grid lg:grid-cols-2 gap-5">
          <section className="bg-white border border-slate-200 rounded-2xl p-5">
            <h2 className="font-heading font-bold mb-4">My class sessions</h2>
            <div className="space-y-3">
              {(data?.sessions || []).map((session) => {
                const marked = sessionAttendance[session.id];
                return <div key={session.id} className="rounded-xl border border-slate-200 p-4">
                  <div className="flex justify-between gap-3"><div><p className="font-semibold">{session.title}</p><p className="text-xs text-slate-500 mt-1">{session.session_date} · {session.start_time}–{session.end_time}</p></div>{marked && <span className="h-fit text-[10px] font-bold rounded-full bg-status-green/10 text-status-green px-2 py-1">PRESENT</span>}</div>
                  {session.room && <p className="text-xs text-slate-500 mt-2">Room / Link: {session.room}</p>}
                  {session.self_attendance_enabled && !marked && <div className="flex gap-2 mt-3"><Input value={codes[session.id] || ""} onChange={(e) => setCodes({ ...codes, [session.id]: e.target.value.toUpperCase() })} placeholder="Attendance code" maxLength={12} /><Button disabled={busy === session.id} onClick={() => markAttendance(session.id)}>Mark present</Button></div>}
                </div>;
              })}
              {!data?.sessions?.length && <p className="text-sm text-slate-400 py-8 text-center">No learning sessions assigned.</p>}
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl p-5">
            <h2 className="font-heading font-bold mb-4">My learning progress</h2>
            <div className="space-y-3">{(data?.progress || []).map((item) => <div key={item.id} className="rounded-xl bg-slate-50 p-4"><div className="flex justify-between gap-2"><p className="font-semibold">{item.skill_code || "Curriculum progress"}</p><span className="text-xs font-bold text-status-blue uppercase">{item.rating}</span></div>{item.score != null && <p className="text-xs text-slate-500 mt-1">Score: {item.score}</p>}{item.remarks && <p className="text-sm text-slate-600 mt-2">{item.remarks}</p>}</div>)}{!data?.progress?.length && <p className="text-sm text-slate-400 py-8 text-center">Progress will appear after faculty assessment.</p>}</div>
          </section>
        </div></div>}

        {tab === "lessons" && <div className="space-y-4">
          {(data?.curriculum || []).map((lesson) => {
            const submissions = (data?.submissions || []).filter((s) => s.curriculum_item_id === lesson.id);
            return <section key={lesson.id} className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="flex gap-3"><span className="w-9 h-9 rounded-full bg-brand-coral/10 text-brand-coral grid place-items-center font-bold">{lesson.sequence}</span><div><p className="text-xs text-slate-500">{lesson.unit_title || "Lesson"} · {lesson.skill_code || "Practice"}</p><h2 className="font-heading font-bold text-lg">{lesson.title}</h2></div></div>
              {lesson.description && <p className="text-sm text-slate-600 mt-4"><b>Objective:</b> {lesson.description}</p>}
              {lesson.lesson_content && <div className="mt-4 whitespace-pre-wrap text-sm">{lesson.lesson_content}</div>}
              {lesson.example_words && <div className="mt-4 rounded-xl bg-slate-50 p-3 text-sm"><b>Examples:</b> {lesson.example_words}</div>}
              {lesson.practice_questions && <div className="mt-3 rounded-xl bg-blue-50 p-3 text-sm whitespace-pre-wrap"><b>Practice questions</b><br/>{lesson.practice_questions}</div>}
              {lesson.homework && <div className="mt-3 rounded-xl bg-amber-50 p-3 text-sm"><b>Homework:</b> {lesson.homework}</div>}
              {lesson.resource_paths?.length > 0 && <div className="mt-3"><p className="text-sm font-semibold">Lesson attachments</p><div className="flex flex-wrap gap-2 mt-2">{lesson.resource_paths.map((path, index) => <Button key={path} size="sm" variant="outline" onClick={() => openResource(path)}>Open attachment {index + 1}</Button>)}</div></div>}
              <div className="mt-4 border-t pt-4"><p className="font-semibold text-sm">Submit my work sample</p><Input className="mt-2" value={submissionNotes[lesson.id] || ""} onChange={(e) => setSubmissionNotes({ ...submissionNotes, [lesson.id]: e.target.value })} placeholder="Write an answer or note for your teacher"/><input className="mt-2 block text-xs" type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={(e) => setSubmissionFiles({ ...submissionFiles, [lesson.id]: e.target.files?.[0] || null })}/><Button className="mt-3" disabled={busy === `submit-${lesson.id}` || (!submissionNotes[lesson.id]?.trim() && !submissionFiles[lesson.id])} onClick={() => submitWork(lesson.id)}><UploadSimple size={15} className="mr-1"/>Submit work</Button>{submissions.map((s) => <p key={s.id} className="text-xs mt-2 text-slate-500">Submitted {new Date(s.submitted_at).toLocaleString()} · <b>{s.status}</b>{s.feedback ? ` · Teacher: ${s.feedback}` : ""}</p>)}</div>
            </section>;
          })}
          {!data?.curriculum?.length && <div className="bg-white rounded-2xl p-10 text-center text-sm text-slate-400">No lessons have been published yet.</div>}
        </div>}

        {tab === "chat" && <DirectChat role="student"/>}
        {tab === "help" && <OrganizationInbox/>}
        {tab === "profile" && data?.student?.id && <Student360View studentId={data.student.id} variant="student" />}
        {tab === "fees" && <div className="space-y-3"><div className="rounded-2xl bg-white border p-5"><h2 className="font-heading font-bold">Course fee ledger</h2><p className="text-sm text-slate-500 mt-1">Outstanding: <b>{inr(data?.fees_outstanding || 0)}</b>. Online or UPI payment and receipts remain available in the linked Parent Portal.</p></div>{(data?.fee_invoices || []).map((row) => <div key={row.id || row.invoice_id} className="rounded-2xl bg-white border p-4 flex justify-between gap-3"><div><p className="font-semibold">{row.title || "Course fee"}</p><p className="text-xs text-slate-500">Due {row.due_date || "—"}</p></div><b className="text-status-red">{inr(row.outstanding || 0)}</b></div>)}</div>}
      </div>
    </DashboardShell>
  );
}

function Stat({ icon: Icon, label, value }) {
  return <div className="bg-white border border-slate-200 rounded-2xl p-4"><Icon size={20} weight="duotone" className="text-brand-coral"/><p className="font-heading text-xl font-extrabold mt-2">{value}</p><p className="text-xs text-slate-500">{label}</p></div>;
}
